import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { IBankSliceState } from "../../utils/bank.types";
import { ApiStatus } from "../../utils/api.types";
import { addBank, deleteBank, fetchBanks, updateBank, fetchBankById } from "./bankThunk";

/**
 * Bank slice initial state.
 */
const initialState: IBankSliceState = {
    banks: [],
    bankDetails: undefined,
    errorMessage: '',
    isBankCreated: false,
    selectedBankId: '',
    status: ApiStatus.IDLE
}

/**
 * Bank Slice.
 */
const bankSlice = createSlice({
    name: 'bank',
    initialState: initialState,
    reducers: {
        // Selected bank id
        selectedBankID: (state, action) => {
            state.selectedBankId = action.payload || '';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Banks
            .addCase(fetchBanks.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchBanks.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.banks = action.payload.banks || [];
                state.errorMessage = '';
            })
            .addCase(fetchBanks.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch bank by ID
            .addCase(fetchBankById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchBankById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.bankDetails = action.payload || undefined;
                state.errorMessage = '';
            })
            .addCase(fetchBankById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Add Bank
            .addCase(addBank.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addBank.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.isBankCreated = true;
                state.banks.push(action.payload.bank);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addBank.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isBankCreated = false;
                state.errorMessage = action.error.message
            })

            // Delete Bank
            .addCase(deleteBank.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteBank.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.banks = state.banks.filter(item => item._id !== id)

                toast.success(message);
            })
            .addCase(deleteBank.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })

            // Update bank
            .addCase(updateBank.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateBank.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, bank: updatedBank } = action.payload
                state.banks = state.banks
                    .map(item => (item._id === item._id ? { ...item, ...updateBank } : item));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateBank.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
    }
});

export default bankSlice.reducer;
